import { useState } from "react";
import { Trans } from "react-i18next";
import classNames from "classnames";
import { t } from "i18next";
import { UserProductImageSource } from "@apis/generated/openapi";
import { ClipModal } from "@components/ClipModal/ClipModal";
import { useGetProductInfo } from "@hooks/useGetProductInfo";
import {
  MAX_PRODUCT_IMAGES,
  type ProductImageFile,
  type ProductImageResponse,
} from "@pages/library/CreateProductEntityModal/types";
import { CreatedProductsPanel } from "./CreatedProductsPanel";
import { SavedProductsPanel } from "./SavedProductsPanel";
import type { SelectedProductPayload, SelectProductTab } from "./types";

export type { SelectedProductPayload, SelectProductTab } from "./types";

export interface SelectProductModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (payload: SelectedProductPayload) => void;
  /** 默认 tab，缺省为 created */
  defaultTab?: SelectProductTab;
}

const TABS: { key: SelectProductTab; label: string }[] = [
  { key: "created", label: "Created" },
  { key: "saved", label: "Saved" },
];

/**
 * Add From Library 弹窗 — 设计稿节点 4855:9243（Created）、4855:12775（Saved）、4898:9306（空态）。
 *
 * 选中商品后立刻 onSelect 并关闭，与 SavedProductPicker 的行为保持一致。
 */
export const SelectProductModal = ({ open, onClose, onSelect, defaultTab = "created" }: SelectProductModalProps) => {
  const [activeTab, setActiveTab] = useState<SelectProductTab>(defaultTab);
  const [enrichingId, setEnrichingId] = useState<string | null>(null);
  const getProductInfo = useGetProductInfo();

  const handleSelect = async (payload: SelectedProductPayload) => {
    if (payload.origin === "library-saved") {
      setEnrichingId(payload.productId);
      try {
        const parsed = await getProductInfo({ id: payload.productId, country: payload.raw?.country });
        if (!parsed) {
          setEnrichingId(null);
          return;
        }
        const { images: parsedImages, product, linkSource } = parsed;
        const category = [product?.priCateName, product?.secCateName, product?.terCateName].filter(Boolean).join(" > ");
        const imageFiles: ProductImageFile[] = (parsedImages ?? [])
          .filter((item) => item.success && item.imageUrl)
          .slice(0, MAX_PRODUCT_IMAGES)
          .map((item) => ({
            uid: `enriched-${item.assetId ?? 0}-${Date.now()}`,
            name: "product-image",
            url: item.imageUrl!,
            response: {
              assetId: item.assetId,
              description: item.description ?? "",
              aiAnalyzed: true,
              source: UserProductImageSource.KALODATA,
            } satisfies ProductImageResponse,
          }));
        onSelect({
          ...payload,
          enriched: {
            title: product?.productTitle ?? "",
            category,
            country: product?.country,
            imageFiles,
            linkSource,
          },
        });
        onClose();
      } catch {
        setEnrichingId(null);
      }
    } else {
      onSelect(payload);
      onClose();
    }
  };

  return (
    <ClipModal
      open={open}
      onCancel={enrichingId ? undefined : onClose}
      width={720}
      title={t("Select Product")}
      styles={{ body: { padding: 0 } }}
    >
      <div className="flex flex-col items-start gap-[8px] px-[24px] pb-[8px] pt-[12px]">
        <div className="flex h-[32px] items-center gap-[32px]">
          {TABS.map((tab) => {
            const isActive = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={classNames(
                  "flex cursor-pointer items-center justify-center border-0 bg-transparent px-0 py-[4px] transition-colors",
                  isActive ? "text-kds-fg" : "text-kds-fg-subtle hover:text-kds-fg-soft",
                )}
              >
                <span className="text-[16px] font-semibold leading-[24px] whitespace-nowrap">
                  <Trans>{tab.label}</Trans>
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        {activeTab === "created" ? (
          <CreatedProductsPanel onSelect={handleSelect} />
        ) : (
          <SavedProductsPanel onSelect={handleSelect} enrichingId={enrichingId} />
        )}
      </div>
    </ClipModal>
  );
};
